import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import * as z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ArrowRight,
  CheckCircle2,
  Cpu,
  Download,
  Loader2,
  LockKeyhole,
  Network,
  Server,
  ShieldCheck,
  Workflow,
} from 'lucide-react';
import MainLayout from '@/components/layout/MainLayout';
import { Header } from '@/components/layout/Header';
import CorporateFooter from '@/components/layout/CorporateFooter';
import SEO from '@/components/common/SEO';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';

const formSchema = z.object({
  companyName: z.string().min(2, 'Organization name is required.'),
  contactName: z.string().min(2, 'Your name is required.'),
  contactEmail: z.string().email('Invalid email address.'),
  website: z.string().url('Please enter a valid website URL (e.g., https://example.com)').optional().or(z.literal('')),
  message: z.string().min(20, 'Please share a short summary of your deployment, infrastructure, or training goals.').max(2000),
});

const capabilityCards = [
  {
    icon: LockKeyhole,
    title: 'Private Deployment',
    description: 'Deploy in dedicated, air-gapped, or internal environments built around your operational requirements.',
  },
  {
    icon: ShieldCheck,
    title: 'Sovereign Infrastructure',
    description: 'Keep hosting strategy, jurisdiction, and infrastructure control aligned with your organization’s needs.',
  },
  {
    icon: Cpu,
    title: 'Model Training',
    description: 'Adapt and train models for domain workflows, internal data, and organization-specific performance targets.',
  },
] as const;

const enterpriseRealities = [
  {
    title: 'Data control',
    description: 'Keep sensitive workloads inside approved environments with clear ownership over where systems run.',
  },
  {
    title: 'Deployment flexibility',
    description: 'Support private cloud, dedicated infrastructure, edge delivery, or tightly governed internal hosting.',
  },
  {
    title: 'Secure environments',
    description: 'Design around regulated, restricted, or institution-specific operating conditions from the start.',
  },
  {
    title: 'Internal ownership',
    description: 'Build capability that your team can govern, operate, and extend over time.',
  },
  {
    title: 'Implementation support',
    description: 'Move from architecture and planning into rollout with practical technical guidance.',
  },
  {
    title: 'Practical delivery',
    description: 'Keep scope focused on deployment, adoption, and measurable business requirements instead of hype.',
  },
] as const;

const solutionCards = [
  {
    icon: Server,
    title: 'Private environments',
    description: 'Scope dedicated deployments for organizations that need tighter control over hosting, access, and operations.',
  },
  {
    icon: Network,
    title: 'Sovereign hosting strategy',
    description: 'Plan infrastructure around jurisdiction, resilience, procurement realities, and long-term control.',
  },
  {
    icon: Cpu,
    title: 'Model training and adaptation',
    description: 'Support model tuning, domain adaptation, and internal use cases that require more than an off-the-shelf setup.',
  },
  {
    icon: Workflow,
    title: 'Deployment planning and rollout',
    description: 'Align technical scope, implementation sequencing, and enterprise stakeholders before full deployment.',
  },
] as const;

const EnterprisePage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const capabilitiesRef = useRef<HTMLElement | null>(null);
  const contactRef = useRef<HTMLElement | null>(null);

  const scrollToSection = (ref: React.RefObject<HTMLElement>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const downloadDeck = () => {
    const a = document.createElement('a');
    a.href = '/pdfs/partnership-deck.pdf';
    a.download = 'CanXP-Enterprise-Overview.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      companyName: '',
      contactName: '',
      contactEmail: '',
      website: '',
      message: '',
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsLoading(true);
    try {
      const { error } = await supabase.functions.invoke('send-partnership-request', {
        body: values,
      });

      if (error) {
        throw new Error(error.message);
      }

      toast({
        title: 'Request received',
        description: "Thanks. We'll review your requirements and follow up shortly.",
      });
      form.reset();
    } catch (error) {
      const err = error as Error;
      toast({
        title: 'Error',
        description: `Something went wrong: ${err.message}`,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <MainLayout>
      <SEO
        title="Enterprise Infrastructure & Deployment | CanXP AI"
        description="Private deployment, sovereign infrastructure, and model training for organizations that need control, security, and practical implementation support."
        url="/enterprise"
      />

      <div className="bg-background text-foreground">
        <Header />

        <section className="relative overflow-hidden px-4 pb-16 pt-20 md:pb-24 md:pt-24">
          <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top,rgba(220,38,38,0.18),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(220,38,38,0.12),transparent_28%)]" />
          <div className="container mx-auto max-w-7xl">
            <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="max-w-3xl"
              >
                <Badge variant="outline" className="border-primary/25 bg-background/70 text-primary backdrop-blur">
                  Enterprise
                </Badge>
                <h1 className="mt-6 text-4xl font-semibold tracking-tight md:text-6xl">
                  Private AI infrastructure for serious organizations.
                </h1>
                <p className="mt-6 max-w-2xl text-lg leading-8 text-muted-foreground">
                  CanXP AI helps enterprises, institutions, and regulated teams deploy private systems, plan sovereign
                  infrastructure, and train models for real operating environments.
                </p>
                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <Button size="lg" onClick={() => scrollToSection(contactRef)} className="gap-2">
                    Request a Demo
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                  <Button size="lg" variant="outline" onClick={downloadDeck} className="gap-2">
                    <Download className="h-4 w-4" />
                    Download Overview
                  </Button>
                </div>
                <button
                  type="button"
                  onClick={() => scrollToSection(capabilitiesRef)}
                  className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                >
                  See enterprise capabilities
                  <ArrowRight className="h-4 w-4" />
                </button>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="rounded-[2rem] border border-primary/15 bg-gradient-to-br from-primary/10 via-background/95 to-background p-6 shadow-[0_24px_120px_-72px_rgba(220,38,38,0.9)] backdrop-blur md:p-8"
              >
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                  <Card className="border-border/70 bg-background/80 backdrop-blur">
                    <CardContent className="p-5">
                      <p className="text-xs font-semibold uppercase tracking-[0.24em] text-primary/80">Deployment</p>
                      <p className="mt-3 text-xl font-semibold tracking-tight">Private by design</p>
                      <p className="mt-2 text-sm leading-6 text-muted-foreground">
                        Dedicated hosting strategies for organizations that cannot compromise on control.
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="border-border/70 bg-background/80 backdrop-blur">
                    <CardContent className="p-5">
                      <p className="text-xs font-semibold uppercase tracking-[0.24em] text-primary/80">Infrastructure</p>
                      <p className="mt-3 text-xl font-semibold tracking-tight">Sovereign aligned</p>
                      <p className="mt-2 text-sm leading-6 text-muted-foreground">
                        Architecture planned around jurisdiction, security, and internal ownership.
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="border-border/70 bg-background/80 backdrop-blur sm:col-span-2 lg:col-span-1 xl:col-span-2">
                    <CardContent className="p-5">
                      <p className="text-xs font-semibold uppercase tracking-[0.24em] text-primary/80">Engagement</p>
                      <p className="mt-3 text-xl font-semibold tracking-tight">Built for implementation</p>
                      <p className="mt-2 text-sm leading-6 text-muted-foreground">
                        Architecture review, deployment planning, and model adaptation framed around enterprise rollout.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        <section ref={capabilitiesRef} id="capabilities" className="px-4 py-8 md:py-12">
          <div className="container mx-auto max-w-7xl">
            <div className="mb-8 max-w-2xl">
              <Badge variant="outline" className="border-primary/25 bg-primary/5 text-primary">
                Enterprise capabilities
              </Badge>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight md:text-4xl">
                A focused enterprise offer, built around control and delivery.
              </h2>
            </div>
            <div className="grid gap-6 md:grid-cols-3">
              {capabilityCards.map((card, index) => {
                const Icon = card.icon;
                return (
                  <motion.div
                    key={card.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, delay: 0.08 * index }}
                  >
                    <Card className="h-full border-border/70 bg-background/80 shadow-[0_18px_60px_-40px_rgba(220,38,38,0.6)] backdrop-blur">
                      <CardHeader>
                        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary">
                          <Icon className="h-5 w-5" />
                        </div>
                        <CardTitle className="text-2xl tracking-tight">{card.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm leading-7 text-muted-foreground">{card.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="px-4 py-20">
          <div className="container mx-auto max-w-7xl">
            <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55 }}
                className="max-w-2xl"
              >
                <Badge variant="outline" className="border-primary/25 bg-primary/5 text-primary">
                  Built for enterprise realities
                </Badge>
                <h2 className="mt-4 text-3xl font-semibold tracking-tight md:text-4xl">
                  Enterprise programs need more than a demo environment.
                </h2>
                <p className="mt-5 text-base leading-7 text-muted-foreground">
                  CanXP AI supports organizations that need clear deployment choices, accountable implementation, and an
                  operating model they can own internally.
                </p>
              </motion.div>

              <div className="grid gap-4 sm:grid-cols-2">
                {enterpriseRealities.map((item, index) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, delay: 0.06 * index }}
                  >
                    <Card className="h-full border-primary/15 bg-background/80 backdrop-blur">
                      <CardContent className="p-5">
                        <div className="flex items-start gap-3">
                          <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-primary" />
                          <div>
                            <h3 className="text-base font-semibold tracking-tight">{item.title}</h3>
                            <p className="mt-2 text-sm leading-6 text-muted-foreground">{item.description}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 py-4 md:py-8">
          <div className="container mx-auto max-w-7xl rounded-[2rem] border border-primary/15 bg-gradient-to-br from-primary/10 via-background to-background p-8 shadow-[0_24px_120px_-72px_rgba(220,38,38,0.9)] md:p-12">
            <div className="mx-auto max-w-3xl text-center">
              <Badge variant="outline" className="border-primary/25 bg-background/70 text-primary">
                How we engage
              </Badge>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight md:text-4xl">
                Support that spans infrastructure planning and implementation.
              </h2>
              <p className="mt-5 text-base leading-7 text-muted-foreground md:text-lg">
                We work with enterprise customers on the decisions that shape deployment success, from hosting strategy
                through rollout planning.
              </p>
            </div>

            <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
              {solutionCards.map((card, index) => {
                const Icon = card.icon;
                return (
                  <motion.div
                    key={card.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, delay: 0.08 * index }}
                  >
                    <Card className="h-full border-primary/15 bg-background/80 backdrop-blur">
                      <CardContent className="p-6">
                        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary">
                          <Icon className="h-5 w-5" />
                        </div>
                        <h3 className="text-xl font-semibold tracking-tight">{card.title}</h3>
                        <p className="mt-3 text-sm leading-6 text-muted-foreground">{card.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="px-4 py-20">
          <div className="container mx-auto max-w-7xl">
            <div className="mb-12 text-center">
              <Badge variant="outline" className="border-primary/25 bg-primary/5 text-primary">
                Enterprise trust
              </Badge>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight md:text-4xl">
                CanXP AI works alongside institutional and infrastructure partners.
              </h2>
              <p className="mx-auto mt-4 max-w-3xl text-base leading-7 text-muted-foreground">
                The enterprise conversation starts with practical deployment requirements and credible implementation
                support.
              </p>
            </div>

            <div className="mb-8 flex flex-wrap items-center justify-center gap-x-12 gap-y-8">
              <a href="https://www.bdc.ca/en" target="_blank" rel="noopener noreferrer">
                <img
                  src="/images/partners/bdc-logo.svg"
                  alt="Business Development Bank of Canada"
                  className="h-16 max-w-full object-contain"
                />
              </a>
              <a href="https://nbif.ca/" target="_blank" rel="noopener noreferrer">
                <img
                  src="/images/partners/nbif-logo.svg"
                  alt="New Brunswick Innovation Foundation"
                  className="h-8 max-w-full object-contain md:h-10"
                />
              </a>
              <a href="https://www.nvidia.com/" target="_blank" rel="noopener noreferrer">
                <img
                  src="/images/partners/nvidia-inception.png"
                  alt="NVIDIA Inception"
                  className="h-28 max-w-full object-contain md:h-32"
                />
              </a>
              <a href="https://www.dell.com/" target="_blank" rel="noopener noreferrer">
                <img src="/images/partners/Dell_Logo.png" alt="Dell Canada" className="h-16 max-w-full object-contain" />
              </a>
              <a href="https://patrii.cloud/" target="_blank" rel="noopener noreferrer">
                <img src="/images/partners/patrii-logo.svg" alt="Patrii Cloud" className="h-16 max-w-full object-contain" />
              </a>
              <a href="https://www.ehconomics.com/" target="_blank" rel="noopener noreferrer">
                <img
                  src="/images/partners/ehconomics-logo.png"
                  alt="EHCOnomics"
                  className="h-16 max-w-full object-contain"
                />
              </a>
            </div>
          </div>
        </section>

        <section ref={contactRef} id="contact" className="px-4 pb-20">
          <div className="container mx-auto max-w-6xl">
            <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
              <div className="rounded-[2rem] border border-primary/15 bg-gradient-to-br from-primary/10 via-background to-background p-8">
                <Badge variant="outline" className="border-primary/25 bg-background/70 text-primary">
                  Start the conversation
                </Badge>
                <h2 className="mt-4 text-3xl font-semibold tracking-tight md:text-4xl">
                  Request an enterprise demo or architecture review.
                </h2>
                <p className="mt-5 text-base leading-7 text-muted-foreground">
                  Share a short overview of your environment, priorities, and deployment goals. We use this first step
                  to route the conversation and come back with the right follow-up.
                </p>
                <div className="mt-8 space-y-5 text-sm leading-7 text-foreground/90">
                  <div>
                    <p className="font-semibold text-foreground">What happens next</p>
                    <p className="mt-1 text-muted-foreground">We review your submission and respond with the right enterprise contact or next-step recommendation.</p>
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">Typical discussion areas</p>
                    <ul className="mt-2 space-y-2 text-muted-foreground">
                      <li>• Private deployment and hosting requirements</li>
                      <li>• Sovereign infrastructure and operating constraints</li>
                      <li>• Model training, adaptation, and rollout planning</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="rounded-[2rem] border border-border/70 bg-card p-8 shadow-[0_18px_60px_-40px_rgba(220,38,38,0.5)]">
                <h3 className="text-2xl font-semibold tracking-tight">Request an Enterprise Demo</h3>
                <p className="mt-3 text-sm leading-7 text-muted-foreground">
                  Tell us what you are planning, where it needs to run, and what kind of follow-up would be most useful.
                </p>

                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="mt-8 space-y-6">
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="companyName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Organization Name</FormLabel>
                            <FormControl>
                              <Input placeholder="Your organization" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="contactName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Contact Name</FormLabel>
                            <FormControl>
                              <Input placeholder="Jane Doe" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={form.control}
                      name="contactEmail"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Work Email</FormLabel>
                          <FormControl>
                            <Input type="email" placeholder="jane.doe@organization.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="website"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Organization Website</FormLabel>
                          <FormControl>
                            <Input placeholder="https://organization.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>What do you need to deploy?</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Describe your environment, hosting requirements, security constraints, or model goals, and what you want CanXP AI to help deliver."
                              className="min-h-[150px]"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button type="submit" className="w-full" disabled={isLoading}>
                      {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Request Enterprise Follow-Up'}
                    </Button>
                  </form>
                </Form>
              </div>
            </div>
          </div>
        </section>

        <CorporateFooter />
      </div>
    </MainLayout>
  );
};

export default EnterprisePage;
